// components/building/WalkScoreBadge.tsx
/**
 * @mockup /design/mockups/01-mobile-building-landing.png
 * @description Walk/Transit/Bike score badges with color-coded display
 * Design System: Warm Sophistication - JetBrains Mono for scores
 */
import { cn } from '../../lib/utils';

interface WalkScoreBadgeProps {
  score: number | null;
  label: string;
  description?: string | null;
}

// Get score color based on value - using design system semantic colors
function getScoreColorClass(score: number): string {
  if (score >= 90) return 'bg-success';
  if (score >= 70) return 'bg-[#5B9E76]'; // Good - slightly lighter green
  if (score >= 50) return 'bg-warning';
  return 'bg-error';
}

export function WalkScoreBadge({ score, label, description }: WalkScoreBadgeProps) {
  if (score === null) return null;

  return (
    <div className="flex flex-col items-center" aria-label={`${label}: ${score}`}>
      <div
        className={cn(
          'w-12 h-12 rounded-lg flex items-center justify-center text-text-inverse font-semibold text-price-sm mb-2',
          getScoreColorClass(score)
        )}
      >
        {score}
      </div>
      <span className="text-label-md text-text-secondary font-medium">{label}</span>
      {description && (
        <span className="text-label-sm text-text-muted mt-0.5">{description}</span>
      )}
    </div>
  );
}

interface ScoreSectionProps {
  walkScore: number | null;
  walkScoreDescription?: string | null;
  transitScore: number | null;
  transitScoreDescription?: string | null;
  bikeScore: number | null;
  bikeScoreDescription?: string | null;
}

export function ScoreSection({
  walkScore,
  walkScoreDescription,
  transitScore,
  transitScoreDescription,
  bikeScore,
  bikeScoreDescription,
}: ScoreSectionProps) {
  // Don't render if no scores available
  if (!walkScore && !transitScore && !bikeScore) {
    return null;
  }

  return (
    <div className="flex justify-center items-start gap-8 w-full border-b border-border pb-6 px-4">
      <WalkScoreBadge
        score={walkScore}
        label="Walk Score"
        description={walkScoreDescription}
      />
      <WalkScoreBadge
        score={transitScore}
        label="Transit"
        description={transitScoreDescription}
      />
      <WalkScoreBadge
        score={bikeScore}
        label="Bike"
        description={bikeScoreDescription}
      />
    </div>
  );
}
